import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Info, RefreshCw, Users, CheckSquare, Eye, Printer, Download, Upload, Calendar, X, FileSpreadsheet, CheckCircle } from "lucide-react";
import AppShell from "@/components/layout/AppShell";
import PageHeroCarousel from "@/components/common/PageHeroCarousel";
import { HERO_IMAGES } from "@/components/common/PageHero";
import FeatureGate from "@/components/FeatureGate";
import InAppBulkGrid from "@/components/children-records/InAppBulkGrid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RECORDS_CAROUSEL = [
  { url: HERO_IMAGES.bulkUpload, alt: "Bulk Upload dashboard" },
  { url: `${BACKEND_URL}/api/carousel/bulk_upload_1.png`, alt: "Data table with children records" },
  { url: `${BACKEND_URL}/api/carousel/bulk_upload_2.png`, alt: "Bulk Upload interface" },
  { url: `${BACKEND_URL}/api/carousel/bulk_upload_3.png`, alt: "Bulk Upload form" },
  { url: `${BACKEND_URL}/api/carousel/bulk_upload_4.png`, alt: "Workflow illustration" },
  { url: `${BACKEND_URL}/api/carousel/bulk_upload_5.png`, alt: "Team collaborating with data" },
  { url: `${BACKEND_URL}/api/carousel/bulk_upload_6.png`, alt: "Cloud data upload" },
  { url: `${BACKEND_URL}/api/carousel/bulk_upload_7.png`, alt: "Cloud upload dashboard" },
  { url: `${BACKEND_URL}/api/carousel/bulk_upload_8.png`, alt: "Data table in classroom" },
  { url: `${BACKEND_URL}/api/carousel/bulk_upload_9.png`, alt: "Data interface in classroom" },
  { url: "https://customer-assets.emergentagent.com/job_218b6b44-61cc-41e4-8731-b85bf1aa4557/artifacts/o72am582_image.png", alt: "Bulk upload in classroom" },
];

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    const [year, month, day] = parts;
    return `${month}/${day}/${year}`;
  }
  return dateStr;
};

const REQUIRED_FORMS = [
  { id: 'enrollment_info', name: 'Enrollment Information Sheet', renewalMonths: 12, renewalLabel: 'Yearly' },
  { id: 'financial_contract', name: 'Financial & Contract Agreement', renewalMonths: 6, renewalLabel: 'Every 6 Months' },
  { id: 'about_child', name: 'All About Your Child Form', renewalMonths: 0, renewalLabel: 'Optional — No Expiration', isOptional: true },
  { id: 'health_report', name: 'Child Health Report', renewalMonths: 12, renewalLabel: 'Yearly' },
  { id: 'shot_records', name: 'Child Shot Records', renewalMonths: 12, renewalLabel: 'Yearly' },
  { id: 'emergency_consent', name: 'Emergency Contact/Parental Consent Form', renewalMonths: 6, renewalLabel: 'Every 6 Months' },
  { id: 'transportation_policy', name: 'School Age Transportation & Transfer of Care Policy', renewalMonths: 12, renewalLabel: 'Yearly' }
];

// For school-age / after-school children, health_report and shot_records are "On File" only
const ON_FILE_FORMS_FOR_SCHOOL_AGE = ['health_report', 'shot_records'];

function getFormsForChild(childCategory) {
  const cat = (childCategory || '').toLowerCase();
  const isSchoolAge = cat.includes('school') || cat === 'after_school';
  if (!isSchoolAge) return REQUIRED_FORMS;
  return REQUIRED_FORMS.map(f =>
    ON_FILE_FORMS_FOR_SCHOOL_AGE.includes(f.id)
      ? { ...f, renewalMonths: 0, renewalLabel: 'On File' }
      : f
  );
}

export default function RecordsBulkUploadPage() {
  const [activeTab, setActiveTab] = useState("bulk-upload");

  // Grid data
  const [children, setChildren] = useState([]);
  const [childRecords, setChildRecords] = useState({});
  const [loadingGrid, setLoadingGrid] = useState(false);

  // ACTION card state
  const [selectedChildren, setSelectedChildren] = useState(new Set());
  const [globalReturnByDate, setGlobalReturnByDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [bulkEmailSending, setBulkEmailSending] = useState(false);
  const [emailPreviewData, setEmailPreviewData] = useState([]);
  const [showEmailPreview, setShowEmailPreview] = useState(false);

  // Template card state
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [bulkDateUploading, setBulkDateUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const fetchChildrenAndRecords = async () => {
    setLoadingGrid(true);
    try {
      const res = await axios.get(`${API}/children`);
      const kids = (res.data || []).filter(c => c.active !== false);
      setChildren(kids);
      // Fetch all records in parallel instead of one-by-one
      const results = await Promise.all(
        kids.map(child =>
          axios.get(`${API}/children-records/${child.id}`)
            .then(r => ({ id: child.id, forms: r.data?.forms || {} }))
            .catch(() => ({ id: child.id, forms: {} }))
        )
      );
      const records = {};
      results.forEach(r => { records[r.id] = r.forms; });
      setChildRecords(records);
    } catch (err) {
      console.error("Error fetching children:", err);
      toast.error("Failed to load children");
    } finally {
      setLoadingGrid(false);
    }
  };

  useEffect(() => { fetchChildrenAndRecords(); }, []);

  const getOutstandingForms = (childId) => {
    const records = childRecords[childId] || {};
    return REQUIRED_FORMS.filter(form => {
      const rec = records[form.id];
      if (!rec) return false;
      if (rec.notApplicable || rec.lastSubmitted === "N/A") return false;
      return rec.outstanding === true;
    });
  };

  const applyGlobalReturnByToAllSelected = async () => {
    if (!globalReturnByDate) { toast.error('Please set a Return By date first'); return; }
    if (selectedChildren.size === 0) { toast.error('Please select children first'); return; }
    const childrenToUpdate = children.filter(c => selectedChildren.has(c.id) && getOutstandingForms(c.id).length > 0);
    if (childrenToUpdate.length === 0) { toast.error('No selected children have outstanding forms'); return; }
    if (!window.confirm(`Apply Return By date "${globalReturnByDate}" to all outstanding forms for ${childrenToUpdate.length} child(ren)?`)) return;
    setSaving(true);
    let successCount = 0;
    for (const child of childrenToUpdate) {
      const currentRecords = childRecords[child.id] || {};
      const updatedForms = { ...currentRecords };
      REQUIRED_FORMS.forEach(form => {
        if (currentRecords[form.id]?.outstanding) {
          updatedForms[form.id] = { ...currentRecords[form.id], returnBy: globalReturnByDate, updatedAt: new Date().toISOString() };
        }
      });
      setChildRecords(prev => ({ ...prev, [child.id]: updatedForms }));
      try { await axios.put(`${API}/children-records/${child.id}`, { forms: updatedForms }); successCount++; } catch {}
    }
    setSaving(false);
    toast.success(`Return By date applied to ${successCount} child(ren)`);
  };

  const applyGlobalReturnByToAllChildren = async () => {
    if (!globalReturnByDate) { toast.error('Please set a Return By date first'); return; }
    const childrenWithOutstanding = children.filter(c => getOutstandingForms(c.id).length > 0);
    if (childrenWithOutstanding.length === 0) { toast.error('No children have outstanding forms'); return; }
    if (!window.confirm(`Apply Return By date "${globalReturnByDate}" to ALL ${childrenWithOutstanding.length} children with outstanding forms?`)) return;
    setSaving(true);
    let successCount = 0;
    for (const child of childrenWithOutstanding) {
      const currentRecords = childRecords[child.id] || {};
      const updatedForms = { ...currentRecords };
      REQUIRED_FORMS.forEach(form => {
        if (currentRecords[form.id]?.outstanding) {
          updatedForms[form.id] = { ...currentRecords[form.id], returnBy: globalReturnByDate, updatedAt: new Date().toISOString() };
        }
      });
      setChildRecords(prev => ({ ...prev, [child.id]: updatedForms }));
      try { await axios.put(`${API}/children-records/${child.id}`, { forms: updatedForms }); successCount++; } catch {}
    }
    setSaving(false);
    toast.success(`Return By date applied to ALL ${successCount} children with outstanding forms`);
  };

  const prepareBulkEmailPreview = () => {
    const childrenWithOutstanding = children.filter(child => selectedChildren.has(child.id) && getOutstandingForms(child.id).length > 0);
    if (childrenWithOutstanding.length === 0) { toast.error('No selected children have outstanding forms'); return; }
    const previewData = childrenWithOutstanding.map(child => {
      const parentEmail = child.pickupPersons?.find(p => p.email)?.email;
      return {
        childId: child.id,
        childName: child.name || `${child.firstName || ''} ${child.lastName || ''}`.trim(),
        parentEmail: parentEmail || null,
        hasEmail: !!parentEmail,
        outstandingForms: getOutstandingForms(child.id).map(f => ({
          name: f.name, renewalLabel: f.renewalLabel,
          returnBy: childRecords[child.id]?.[f.id]?.returnBy || null
        }))
      };
    });
    setEmailPreviewData(previewData);
    setShowEmailPreview(true);
  };

  const confirmAndSendBulkEmails = async () => {
    const childrenToEmail = emailPreviewData.filter(item => item.hasEmail);
    if (childrenToEmail.length === 0) { toast.error('No children with valid email addresses'); setShowEmailPreview(false); return; }
    setShowEmailPreview(false);
    setBulkEmailSending(true);
    let successCount = 0;
    for (const item of childrenToEmail) {
      const child = children.find(c => c.id === item.childId);
      if (!child) continue;
      try {
        await axios.post(`${API}/children-records/send-email`, {
          childId: child.id,
          childName: item.childName,
          parentEmail: item.parentEmail,
          outstandingForms: getOutstandingForms(child.id).map(f => ({
            name: f.name, renewalLabel: f.renewalLabel,
            lastSubmitted: childRecords[child.id]?.[f.id]?.lastSubmitted || null,
            returnBy: childRecords[child.id]?.[f.id]?.returnBy || null
          })),
          centerInfo: {}
        });
        successCount++;
        await new Promise(resolve => setTimeout(resolve, 600));
      } catch {}
    }
    setBulkEmailSending(false);
    setSelectedChildren(new Set());
    setEmailPreviewData([]);
    toast.success(`Successfully sent ${successCount} email(s)`);
  };

  const printFullReport = () => {
    const childrenToReport = selectedChildren.size > 0
      ? children.filter(c => selectedChildren.has(c.id))
      : children.filter(c => getOutstandingForms(c.id).length > 0);
    if (childrenToReport.length === 0) { toast.error('No children with outstanding records to print'); return; }
    const childrenHtml = childrenToReport.map(child => {
      const outstandingForms = getOutstandingForms(child.id);
      const rows = outstandingForms.map(form => {
        const rec = childRecords[child.id]?.[form.id] || {};
        return `<tr><td>☐ ${form.name}</td><td>${form.renewalLabel}</td><td>${formatDate(rec.lastSubmitted) || 'Never'}</td><td>${formatDate(rec.returnBy) || 'Not set'}</td></tr>`;
      }).join('');
      const name = child.name || `${child.firstName || ''} ${child.lastName || ''}`.trim();
      return `<h3>${name} — ${outstandingForms.length} outstanding</h3><table border="1" cellpadding="4">${rows}</table>`;
    }).join('<hr/>');
    const win = window.open('', '_blank');
    win.document.write(`<html><body><h2>Outstanding Records Report</h2>${childrenHtml}</body></html>`);
    win.print();
  };

  const downloadFullReportPDF = async () => {
    const childrenToReport = selectedChildren.size > 0
      ? children.filter(c => selectedChildren.has(c.id))
      : children.filter(c => getOutstandingForms(c.id).length > 0);
    if (childrenToReport.length === 0) { toast.error('No children with outstanding records to export'); return; }
    try {
      toast.info('Generating PDF report...');
      const reportData = childrenToReport.map(child => ({
        childId: child.id,
        childName: child.name || `${child.firstName || ''} ${child.lastName || ''}`.trim(),
        category: child.category?.replace('_', ' ') || 'Not assigned',
        parentEmail: child.pickupPersons?.find(p => p.email)?.email || null,
        outstandingForms: getOutstandingForms(child.id).map(f => ({
          name: f.name, renewalLabel: f.renewalLabel,
          lastSubmitted: childRecords[child.id]?.[f.id]?.lastSubmitted || null,
          returnBy: childRecords[child.id]?.[f.id]?.returnBy || null
        })),
        currentForms: []
      }));
      const response = await axios.post(`${API}/children-records/generate-full-report-pdf`,
        { children: reportData, centerInfo: {}, generatedDate: new Date().toISOString() },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Children_Records_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF report downloaded');
    } catch { toast.error('Failed to generate PDF'); }
  };

  const downloadBulkDateTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const response = await axios.get(`${API}/children-records/bulk-date-template`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Date_on_Form_Template_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded successfully');
    } catch {
      toast.error('Failed to download template');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.name.endsWith('.xlsx') || droppedFile.name.endsWith('.xls')) {
        setSelectedFile(droppedFile);
      } else {
        toast.error('Please upload an Excel file (.xlsx or .xls)');
      }
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        setSelectedFile(file);
      } else {
        toast.error('Please upload an Excel file (.xlsx or .xls)');
      }
    }
  };

  const handleUploadSelected = async () => {
    if (!selectedFile) return;
    setBulkDateUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      const response = await axios.post(`${API}/children-records/bulk-date-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.updatedRecords) {
        for (const [childId, forms] of Object.entries(response.data.updatedRecords)) {
          setChildRecords(prev => ({ ...prev, [childId]: { ...prev[childId], ...forms } }));
        }
      }
      toast.success(`Successfully updated dates for ${response.data.childrenUpdated} children`);
      if (response.data.skippedDates && response.data.skippedDates.length > 0) {
        response.data.skippedDates.forEach(msg => toast.error(msg, { duration: 10000 }));
      }
      setSelectedFile(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to process upload");
    } finally {
      setBulkDateUploading(false);
    }
  };

  const handleClearFile = () => {
    setSelectedFile(null);
  };

  const handleBulkDateUpload = async (e) => {
    const uploadFile = e.target.files?.[0];
    if (!uploadFile) return;

    if (!uploadFile.name.endsWith('.xlsx') && !uploadFile.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setBulkDateUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      const response = await axios.post(`${API}/children-records/bulk-date-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Update local childRecords state with results
      if (response.data.updatedRecords) {
        for (const [childId, forms] of Object.entries(response.data.updatedRecords)) {
          setChildRecords(prev => ({ ...prev, [childId]: { ...prev[childId], ...forms } }));
        }
      }

      toast.success(`Successfully updated dates for ${response.data.childrenUpdated} children`);
      if (response.data.skippedDates && response.data.skippedDates.length > 0) {
        response.data.skippedDates.forEach(msg => toast.error(msg, { duration: 10000 }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to process upload");
    } finally {
      setBulkDateUploading(false);
      e.target.value = "";
    }
  };

  return (
    <AppShell pageTitle="Compliance Records Bulk Date Entry" breadcrumb>
      <PageHeroCarousel
        images={RECORDS_CAROUSEL}
        title="Compliance Records Bulk Date Entry"
        subtitle="Update child compliance dates in bulk or use the mass upload template"
        altText="Records bulk upload"
      />

      <FeatureGate feature="child_compliance_uploads">
        <div className="relative z-10">

          {/* Tab Navigation */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setActiveTab("bulk-upload")}
              data-testid="records-bulk-tab-in-app"
              className={`px-5 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                activeTab === "bulk-upload"
                  ? "bg-teal-600 text-white shadow-sm"
                  : "bg-white text-teal-700 border border-teal-300 hover:bg-teal-50"
              }`}
            >
              Bulk Upload
            </button>
            <button
              onClick={() => setActiveTab("mass-template")}
              data-testid="records-bulk-tab-template"
              className={`px-5 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                activeTab === "mass-template"
                  ? "bg-teal-600 text-white shadow-sm"
                  : "bg-white text-teal-700 border border-teal-300 hover:bg-teal-50"
              }`}
            >
              Bulk Upload Template
            </button>
          </div>

          {/* ── Tab 1: In-App Bulk Upload Grid ── */}
          {activeTab === "bulk-upload" && (
            <div className="space-y-4">
              <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-teal-700">
                    <p className="font-semibold mb-2">How to use Bulk Upload:</p>
                    <ol className="list-decimal list-inside space-y-1 text-teal-600">
                      <li>All active children load automatically as columns. Click <strong>Refresh</strong> to pick up any newly enrolled children.</li>
                      <li>Existing dates are pre-filled from each child's record — edit any cell directly.</li>
                      <li>Enter the date a form was received for each child. Leave blank for forms not yet received.</li>
                      <li>Click <strong>Save All Changes</strong> — dates save directly into each child's record in Child Records &amp; Documents.</li>
                      <li>Archived children are automatically excluded from this grid.</li>
                    </ol>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <InAppBulkGrid
                  children={children}
                  childRecords={childRecords}
                  onRecordsUpdated={fetchChildrenAndRecords}
                  onRefresh={fetchChildrenAndRecords}
                  loading={loadingGrid}
                />
              </div>
            </div>
          )}

          {/* ── Tab 2: Bulk Upload Template ── */}
          {activeTab === "mass-template" && (
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="bg-white p-3 rounded-lg border border-green-100 mb-4">
                  <div className="flex items-start gap-2">
                    <Info className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-green-700">
                      <p className="font-medium mb-1">Bulk Upload to Auto Fill Date on Form Area:</p>
                      <ol className="list-decimal list-inside space-y-1 text-green-600">
                        <li>Click <strong>"Download Template"</strong> to get an Excel file with all children and forms</li>
                        <li>Fill in dates in the template using format <strong>MM/DD/YYYY</strong> (e.g., 01/15/2026)</li>
                        <li>Upload the completed template to automatically update all "Date on Form" fields</li>
                      </ol>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-semibold text-green-800 flex items-center gap-2">
                    <Upload className="w-5 h-5" />
                    Bulk Upload to Auto Fill Date on Form
                  </h3>
                  <Button onClick={downloadBulkDateTemplate} size="sm" variant="outline" className="border-green-300 text-green-700 hover:bg-green-100" disabled={downloadingTemplate} data-testid="download-date-template-btn">
                    <Download className="w-4 h-4 mr-1" />
                    {downloadingTemplate ? 'Downloading...' : 'Download Template'}
                  </Button>
                  <div className="relative">
                    <input type="file" id="bulk-date-upload" className="hidden" accept=".xlsx,.xls" onChange={handleBulkDateUpload} />
                    <Button onClick={() => document.getElementById('bulk-date-upload').click()} size="sm" className="bg-green-600 hover:bg-green-700 text-white" disabled={bulkDateUploading} data-testid="upload-date-template-btn">
                      <Upload className="w-4 h-4 mr-1" />
                      {bulkDateUploading ? 'Uploading...' : 'Upload Completed Template'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Drag and Drop Upload Area */}
              <div className="bg-white rounded-lg border-2 border-green-200 p-6">
                <h3 className="text-2xl font-bold text-green-800 mb-4">Upload File</h3>
                <div
                  className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                    dragActive
                      ? 'border-green-500 bg-green-50'
                      : 'border-green-300 hover:border-green-400 hover:bg-green-50'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  {selectedFile ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-center gap-2 text-green-800">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <p className="font-semibold">{selectedFile.name}</p>
                      </div>
                      <p className="text-sm text-green-600">
                        {(selectedFile.size / 1024).toFixed(2)} KB
                      </p>
                      <div className="flex gap-3 justify-center">
                        <Button
                          onClick={handleUploadSelected}
                          disabled={bulkDateUploading}
                          className="bg-green-600 hover:bg-green-700 text-white rounded-full"
                        >
                          {bulkDateUploading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="w-5 h-5 mr-2" />
                              Upload & Import
                            </>
                          )}
                        </Button>
                        <Button
                          onClick={handleClearFile}
                          variant="outline"
                          className="border-green-300 text-green-700 rounded-full"
                        >
                          Clear
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <FileSpreadsheet className="w-16 h-16 text-green-500 mx-auto mb-4" />
                      <p className="text-lg text-green-800 font-semibold">
                        Drag and drop your file here
                      </p>
                      <p className="text-green-600">or</p>
                      <div>
                        <input
                          id="records-file-upload"
                          type="file"
                          accept=".xlsx,.xls"
                          onChange={handleFileSelect}
                          className="hidden"
                          data-testid="records-file-input"
                        />
                        <label
                          htmlFor="records-file-upload"
                          className="inline-flex items-center justify-center px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-full cursor-pointer font-semibold transition-colors"
                        >
                          <Upload className="w-5 h-5 mr-2" />
                          Choose File
                        </label>
                      </div>
                      <p className="text-sm text-green-600">
                        Supports Excel (.xlsx, .xls) files
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Email Preview Modal */}
        {showEmailPreview && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900">Email Preview</h2>
                  <Button onClick={() => setShowEmailPreview(false)} variant="ghost" size="sm">
                    <X className="w-5 h-5" />
                  </Button>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Review the emails below before sending. {emailPreviewData.filter(d => d.hasEmail).length} of {emailPreviewData.length} children have email addresses on file.
                </p>
                <div className="space-y-3 mb-6">
                  {emailPreviewData.map(item => (
                    <div key={item.childId} className={`p-3 rounded-lg border ${item.hasEmail ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-800">{item.childName}</p>
                          <p className="text-sm text-gray-500">{item.hasEmail ? item.parentEmail : 'No email on file'}</p>
                          <p className="text-xs text-gray-400">{item.outstandingForms.length} outstanding form(s)</p>
                        </div>
                        {!item.hasEmail && <span className="text-xs text-red-500 bg-red-50 px-2 py-1 rounded">No email</span>}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-3">
                  <Button onClick={confirmAndSendBulkEmails} className="bg-purple-600 hover:bg-purple-700 text-white flex-1">
                    Send {emailPreviewData.filter(d => d.hasEmail).length} Email(s)
                  </Button>
                  <Button onClick={() => setShowEmailPreview(false)} variant="outline">Cancel</Button>
                </div>
              </div>
            </div>
          </div>
        )}

      </FeatureGate>
    </AppShell>
  );
}
